import "@/App.css";
import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { initWebVitals } from "./utils/webVitals";

import { AuthProvider, useAuth } from "./context/AuthContext";
import { I18nProvider, useI18n } from "./context/I18nContext";
import { InvestorProfileProvider } from "./context/InvestorProfileContext";
import { ProtectedRoute, PublicOnlyRoute, PermissionGuard } from "./components/ProtectedRoute";
import { Toaster } from "./components/ui/sonner";
import { GlobalConfirmHost } from "./lib/confirmGlobal";
import CookieConsent from "./components/CookieConsent";

// LOT 537 v63.2ter — Layouts (eager: only LocalizedPublicLayout, used on every public page).
// AdminLayout / InvestorLayout / SponsorLayout sont désormais lazy-loadés (B).
// ImpersonationBanner reste eager (provider global UX, conformément aux interdictions v63.2ter).
import LocalizedPublicLayout from "./components/LocalizedPublicLayout";
import ErrorBoundary from "./components/ErrorBoundary";
import ImpersonationBanner from "./components/ImpersonationBanner";

// Public Pages — eager: ONLY HomePage and LoginPage (critical entry points).
// Les autres pages publiques (juridiques, auth secondaires, SPV) sont lazy-loadées (C, D, E).
import HomePage from "./pages/public/HomePage";
import LoginPage from "./pages/public/LoginPage";
import PublicSpvGate from "./components/PublicSpvGate";
// LOT 470 — Accessibility helpers
import { SkipToContent } from "./components/a11y";

// Lot 354 — Sober loader for lazy-loaded chunks (visible only briefly between routes)
const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center" data-testid="route-fallback">
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-2 border-bronze/30 border-t-bronze rounded-full animate-spin" />
      <span className="text-xs text-graphite/80 tracking-wider">Chargement…</span>
    </div>
  </div>
);

// Lot 358 — ChunkLoadError handler.
// When the frontend bundle is rebuilt, hashes of lazy chunks change. A user with the
// previous version cached will get "Loading chunk failed" / "ChunkLoadError" the next
// time he navigates into a lazy route. We auto-reload once to pick up the new hashes.
// The reload key is stored in sessionStorage to avoid an infinite reload loop if the
// reload itself fails for another reason.
const lazyWithRetry = (importer) => () =>
  importer().catch((err) => {
    const msg = String(err?.message || err);
    const isChunkErr = /Loading chunk|ChunkLoadError|Loading CSS chunk|Failed to fetch dynamically imported module/i.test(msg);
    if (isChunkErr && typeof window !== "undefined") {
      const key = "__cc_chunk_reload_ts";
      const last = Number(sessionStorage.getItem(key) || "0");
      // Only reload once per minute to avoid loops
      if (Date.now() - last > 60_000) {
        sessionStorage.setItem(key, String(Date.now()));
        window.location.reload();
        return new Promise(() => {});
      }
    }
    throw err;
  });

// Redirect helper: sends bare paths to the correct language-prefixed version
function LangRedirect({ toFr, toEn }) {
  const { lang } = useI18n();
  const { search, hash } = useLocation();
  const target = (lang === 'en' ? toEn : toFr) + (search || '') + (hash || '');
  return <Navigate to={target} replace />;
}

// APEX M1.6 — Redirections legacy : « Mon portefeuille » + « Tous mes projets »
// fusionnés dans « Mon patrimoine ». Conserve la query (?tab=..., ?as=...)
// pour ne casser aucun lien profond (menus, notifications, emails).
function LegacyPatrimoineRedirect() {
  const { search, hash } = useLocation();
  return <Navigate to={`/portal/patrimoine${search || ''}${hash || ''}`} replace />;
}

// SPRINT 6 — contrat « jamais rien » (Constitution, art. 5/7) : une URL
// admin inconnue affiche un état honnête, jamais un espace de travail vide.
function AdminNotFound() {
  return (
    <div className="p-10 text-center" data-testid="admin-not-found">
      <p className="font-heading text-2xl text-charcoal mb-2">Page introuvable</p>
      <p className="text-sm text-graphite/80 mb-6">Cette adresse ne correspond à aucun écran de la console d'administration.</p>
      <a href="/admin/cockpit-os" className="text-sm text-bronze underline hover:text-charcoal" data-testid="admin-not-found-home">Revenir au tableau de pilotage</a>
    </div>
  );
}

// STUDIO B2 — Redirection /p/:slug (sans préfixe langue) → version localisée.
function FreePageBareRedirect() {
  const { lang } = useI18n();
  const { pathname, search, hash } = useLocation();
  return <Navigate to={`/${lang === 'en' ? 'en' : 'fr'}${pathname}${search || ''}${hash || ''}`} replace />;
}

// APEX SPRINT01 B2 — Le Studio est absorbé dans le Workspace projet (cockpit).
// Les anciennes routes (/studio, /page-editor, /preview) redirigent vers
// /admin/projects/:id?space=vitrine en CONSERVANT la query (?tab=media, etc.)
// pour ne casser aucun lien profond (favoris, palette ⌘K, emails, historique).
// APEX SPRINT01 B1/B6 — Redirection vers un hub en CONSERVANT la query
// existante (?new=1, ?edit=<id>, ?statut=…) et en forçant l'onglet cible.
// Garantit qu'aucun lien profond (palette ⌘K, emails, favoris) n'est cassé.
function RedirectWithQuery({ to, params }) {
  const { search, hash } = useLocation();
  const qs = new URLSearchParams(search);
  Object.entries(params || {}).forEach(([k, v]) => qs.set(k, v));
  return <Navigate to={`${to}?${qs.toString()}${hash || ''}`} replace />;
}

function LegacyStudioRedirect({ forceTab }) {
  const { projectId } = useParams();
  const { search, hash } = useLocation();
  const qs = new URLSearchParams(search);
  qs.set('space', 'vitrine');
  if (forceTab && !qs.get('tab')) qs.set('tab', forceTab);
  return <Navigate to={`/admin/projects/${projectId}?${qs.toString()}${hash || ''}`} replace />;
}

// Investor Pages — lazy-loaded (Lot 354 bundle optimization)
const InvestorDashboard = lazy(lazyWithRetry(() => import("./pages/investor/Dashboard")));
const ProjectsPage = lazy(lazyWithRetry(() => import("./pages/investor/Projects")));
const ProjectDetail = lazy(lazyWithRetry(() => import("./pages/investor/ProjectDetail")));
const PatrimoinePage = lazy(lazyWithRetry(() => import("./pages/investor/Patrimoine")));
const WaitingListPage = lazy(lazyWithRetry(() => import("./pages/investor/WaitingList")));
const CompliancePage = lazy(lazyWithRetry(() => import("./pages/investor/Compliance")));
const PortalKybPage = lazy(lazyWithRetry(() => import("./pages/investor/PortalKyb")));
const InvestorDocumentsPage = lazy(lazyWithRetry(() => import("./pages/investor/Documents")));
const MovementsPage = lazy(lazyWithRetry(() => import("./pages/investor/Movements")));
const InvestmentDetailPage = lazy(lazyWithRetry(() => import("./pages/investor/InvestmentDetail")));
const SubscriptionCompletionPage = lazy(lazyWithRetry(() => import("./pages/investor/SubscriptionCompletion")));
const InvestorUpdatesPage = lazy(lazyWithRetry(() => import("./pages/investor/Updates")));
const NotificationsPage = lazy(lazyWithRetry(() => import("./pages/investor/Notifications")));
const ProfilePage = lazy(lazyWithRetry(() => import("./pages/investor/Profile")));
// GO PO V3 — Espace investisseur « Ambassadeur »
const InvestorAmbassadorPage = lazy(lazyWithRetry(() => import("./pages/investor/Ambassador")));
const InvestorSecondaryMarketPage = lazy(lazyWithRetry(() => import("./pages/investor/SecondaryMarket")));
const InvestorReportingCenter = lazy(lazyWithRetry(() => import("./pages/investor/ReportingCenter")));
const NotificationPreferencesPage = lazy(lazyWithRetry(() => import("./pages/investor/NotificationPreferences")));
const InvestorIssuesPage = lazy(lazyWithRetry(() => import("./pages/investor/Issues")));
const InvestorMessagesPage = lazy(lazyWithRetry(() => import("./pages/investor/Messages")));
const InvestorNewsPage = lazy(lazyWithRetry(() => import("./pages/investor/News")));

// Admin Pages — lazy-loaded (Lot 354 bundle optimization)
const AccessRequestsPage = lazy(lazyWithRetry(() => import("./pages/admin/AccessRequests")));
const InvestorsPage = lazy(lazyWithRetry(() => import("./pages/admin/Investors")));
const AdminInvestorDetailPage = lazy(lazyWithRetry(() => import("./pages/admin/InvestorDetail")));
const InvestorPortalPreview = lazy(lazyWithRetry(() => import("./pages/admin/InvestorPortalPreview")));
const AdminMessagesPage = lazy(lazyWithRetry(() => import("./pages/admin/Messages")));
// LOT back-office-compliance-p1 · Phase 3 — Portail conformité back-office
// (remplace le lazy-import orphelin `ComplianceReview.js`, désormais supprimé)
const ComplianceHomePage = lazy(lazyWithRetry(() => import("./pages/admin/ComplianceHome")));
const ComplianceInvestorListPage = lazy(lazyWithRetry(() => import("./pages/admin/ComplianceInvestorList")));
const ComplianceInvestorDetailPage = lazy(lazyWithRetry(() => import("./pages/admin/ComplianceInvestorDetail")));
// OS V3 · Sprint Émilie — file de revue KYC séquentielle + Ma journée + Santé des projets
const ComplianceReviewQueuePage = lazy(lazyWithRetry(() => import("./pages/admin/ComplianceReviewQueue")));
const SiteWorkspacePage = lazy(lazyWithRetry(() => import('./pages/admin/SiteWorkspace')));
const MyDayPage = lazy(lazyWithRetry(() => import("./pages/admin/MyDay")));
const ProjectsHealthPage = lazy(lazyWithRetry(() => import("./pages/admin/ProjectsHealth")));
// FAMILY OFFICE STUDIO — Autonomy Program
const MediaLibraryPage = lazy(lazyWithRetry(() => import("./pages/admin/MediaLibrary")));
// APEX FIABILITÉ — Diagnostic Patrimoine (super_admin) : chaîne de données tracée
const DiagnosticPatrimoinePage = lazy(lazyWithRetry(() => import("./pages/admin/DiagnosticPatrimoine")));
// APEX SPRINT01 B6/B1 — Hubs de fusion (Paramètres 5 sous-pages, Publications 2 canaux)
const SettingsHubPage = lazy(lazyWithRetry(() => import("./pages/admin/SettingsHub")));
const PublicationsHubPage = lazy(lazyWithRetry(() => import("./pages/admin/PublicationsHub")));
// ACQUISITION V3 — écran décisionnel « Acquisition organique » (first-party).
const AcquisitionInsightsPage = lazy(lazyWithRetry(() => import("./pages/admin/AcquisitionInsightsPage")));
// ACQUISITION ENGINE V1 — CRM interne léger (pipeline des leads + fiche lead).
const LeadsPipelinePage = lazy(lazyWithRetry(() => import("./pages/admin/LeadsPipelinePage")));
const LeadDetailPage = lazy(lazyWithRetry(() => import("./pages/admin/LeadDetailPage")));
// PROGRAMME AMBASSADEUR V1 (GO PO 17/09) — console admin + fiche + page publique.
const AmbassadorsPage = lazy(lazyWithRetry(() => import("./pages/admin/ambassadors/AmbassadorsPage")));
const AmbassadorDetail = lazy(lazyWithRetry(() => import("./pages/admin/ambassadors/AmbassadorDetail")));
const ReferralLanding = lazy(lazyWithRetry(() => import("./pages/public/ReferralLanding")));
const AcquisitionExecutivePage = lazy(lazyWithRetry(() => import("./pages/admin/AcquisitionExecutivePage")));
// ACQUISITION ENGINE V1.5 — poste de travail commercial quotidien.
const MaJourneePage = lazy(lazyWithRetry(() => import("./pages/admin/MaJourneePage")));
const FreePagesAdminPage = lazy(lazyWithRetry(() => import("./pages/admin/FreePagesAdmin")));
const FreePageEditorPage = lazy(lazyWithRetry(() => import("./pages/admin/FreePageEditor")));
const FreePagePublic = lazy(lazyWithRetry(() => import("./pages/public/FreePage")));
const PublicNotFoundPage = lazy(lazyWithRetry(() => import("./pages/public/NotFoundPage"))); // SPRINT 8 — vraie 404 publique
const ProjectWizardPage = lazy(lazyWithRetry(() => import("./pages/admin/ProjectWizard")));
const StudioFieldsPage = lazy(lazyWithRetry(() => import("./pages/admin/StudioFields")));
const DocumentCenterPage = lazy(lazyWithRetry(() => import("./pages/admin/DocumentCenter")));
const UpdateCenterPage = lazy(lazyWithRetry(() => import("./pages/admin/UpdateCenter")));
const UpdateDetailPage = lazy(lazyWithRetry(() => import("./pages/admin/UpdateDetail")));
const AuditLogPage = lazy(lazyWithRetry(() => import("./pages/admin/AuditLog")));
const InternalUsersPage = lazy(lazyWithRetry(() => import("./pages/admin/InternalUsers")));
const AdminSecondaryMarketPage = lazy(lazyWithRetry(() => import("./pages/admin/SecondaryMarket")));
const ProjectsAdminPage = lazy(lazyWithRetry(() => import("./pages/admin/Projects")));
const AdminProjectDetailPage = lazy(lazyWithRetry(() => import("./pages/admin/ProjectDetail")));
// PHASE 11 B3 — Workspace Projet : /admin/projects/:id devient l'univers du
// projet ; la console de pilotage historique reste accessible, PAR PROJET, à
// /admin/projects/:id/console (il n'existe AUCUNE route top-level /console —
// la console est toujours contextuelle à un projet). Zéro régression.
const ProjectWorkspacePage = lazy(lazyWithRetry(() => import("./pages/admin/project-workspace/ProjectWorkspace")));
const ProjectStudioPage = lazy(lazyWithRetry(() => import("./pages/admin/ProjectStudio")));
const SubscriptionsAdminPage = lazy(lazyWithRetry(() => import("./pages/admin/Subscriptions")));
const SubscriptionDetailAdminPage = lazy(lazyWithRetry(() => import("./pages/admin/SubscriptionDetail")));
const WaitingListAdminPage = lazy(lazyWithRetry(() => import("./pages/admin/WaitingList")));
const MovementsAdminPage = lazy(lazyWithRetry(() => import("./pages/admin/Movements")));
const AdminReconciliation = lazy(lazyWithRetry(() => import("./pages/admin/AdminReconciliation")));
const ImportWizard = lazy(lazyWithRetry(() => import("./pages/admin/ImportWizard")));
const ReportsAdmin = lazy(lazyWithRetry(() => import("./pages/admin/ReportsAdmin")));
const ProductionHealth = lazy(lazyWithRetry(() => import("./pages/admin/ProductionHealth")));
const DataOps = lazy(lazyWithRetry(() => import("./pages/admin/DataOps")));
const DataHygienePage = lazy(lazyWithRetry(() => import("./pages/admin/DataHygiene")));
const SubscriptionAnomaliesPage = lazy(lazyWithRetry(() => import("./pages/admin/SubscriptionAnomalies")));
const AnomaliesToFixPage = lazy(lazyWithRetry(() => import("./pages/admin/AnomaliesToFix")));  // PHASE 1 · F-3
const KybDashboardPage = lazy(lazyWithRetry(() => import("./pages/admin/KybDashboard")));
const UboRegistryPage = lazy(lazyWithRetry(() => import("./pages/admin/UboRegistry")));
const TaxClassification = lazy(lazyWithRetry(() => import("./pages/admin/TaxClassification")));
const IdentityResolution = lazy(lazyWithRetry(() => import("./pages/admin/IdentityResolution"))); // SPRINT 5 — résolution d'identité PP
const DocumentHealthPage = lazy(lazyWithRetry(() => import("./pages/admin/DocumentHealth"))); // DOC RELIABILITY V1 — santé documentaire (super admin)
const CockpitOS = lazy(lazyWithRetry(() => import("./pages/admin/CockpitOS")));
// PHASE 10 B-1 — hubs de fusion (activité ×3, briefings ×3) + index + cockpit système
const ActivityJournalPage = lazy(lazyWithRetry(() => import("./pages/admin/ActivityJournal")));
const BriefingsHubPage = lazy(lazyWithRetry(() => import("./pages/admin/BriefingsHub")));
const AllFunctionsPage = lazy(lazyWithRetry(() => import("./pages/admin/AllFunctions")));
const SystemCockpitPage = lazy(lazyWithRetry(() => import("./pages/admin/SystemCockpit")));
const StructuralQuality = lazy(lazyWithRetry(() => import("./pages/admin/StructuralQuality")));
// LOT 497 — Diagnostic de session pour Emilie et les autres admins
const SessionDiagnostic = lazy(lazyWithRetry(() => import("./pages/admin/SessionDiagnostic")));
// LOT 498 — Fusion de comptes doublons (super_admin only)
const UsersMerge = lazy(lazyWithRetry(() => import("./pages/admin/account-admin/UsersMerge")));
const SiteEditorPage = lazy(lazyWithRetry(() => import("./pages/admin/SiteEditor")));
const I18nOverridesPage = lazy(lazyWithRetry(() => import("./pages/admin/I18nOverrides")));
const CmsCoverageMapPage = lazy(lazyWithRetry(() => import("./pages/admin/CmsCoverageMap")));
const CmsPricingPage = lazy(lazyWithRetry(() => import("./pages/admin/CmsPricingPage")));
const CmsIndexPage = lazy(lazyWithRetry(() => import("./pages/admin/CmsIndexPage")));
const CmsCompanyInfoPage = lazy(lazyWithRetry(() => import("./pages/admin/CmsCompanyInfoPage")));
const HomepageProjectsAdminPage = lazy(lazyWithRetry(() => import("./pages/admin/HomepageProjectsAdmin")));
const QAConsolePage = lazy(lazyWithRetry(() => import("./pages/admin/QAConsole")));
const ProjectOperationsPage = lazy(lazyWithRetry(() => import("./pages/admin/ProjectOperations")));
const ProjectCommunicationsPage = lazy(lazyWithRetry(() => import("./pages/admin/ProjectCommunications")));
const DistributionManagementPage = lazy(lazyWithRetry(() => import("./pages/admin/DistributionManagement")));
const DistributionCalendarPage = lazy(lazyWithRetry(() => import("./pages/admin/DistributionCalendar")));
const TemplateConsolePage = lazy(lazyWithRetry(() => import("./pages/admin/TemplateConsole")));
const OperationsPipeline = lazy(lazyWithRetry(() => import("./pages/admin/OperationsPipeline")));
// SPRINT 2 §6.2 — AdminPipeline n'est plus routé ici : il vit comme onglet
// lazy de la page Souscriptions (pages/admin/Subscriptions.js).
const SpvIntakes = lazy(lazyWithRetry(() => import("./pages/admin/SpvIntakes")));
const Vehicles = lazy(lazyWithRetry(() => import("./pages/admin/Vehicles")));
const VehicleDetail = lazy(lazyWithRetry(() => import("./pages/admin/VehicleDetail")));
const GoLivePage = lazy(lazyWithRetry(() => import("./pages/admin/GoLive")));
const GoLiveControlCenterPage = lazy(lazyWithRetry(() => import("./pages/admin/GoLiveControlCenterPage")));
const BackupsLot358Page = lazy(lazyWithRetry(() => import("./pages/admin/BackupsLot358")));
const LinkedUserRequests = lazy(lazyWithRetry(() => import("./pages/admin/LinkedUserRequests")));
const HolderRequests = lazy(lazyWithRetry(() => import("./pages/admin/HolderRequests")));
const SponsorOpsPage = lazy(lazyWithRetry(() => import("./pages/admin/SponsorOps")));
const OnboardingConsolePage = lazy(lazyWithRetry(() => import("./pages/admin/OnboardingConsole")));
const MfaEnrollmentPage = lazy(lazyWithRetry(() => import("./pages/admin/MfaEnrollment")));

// Design Lab v2 — lazy-loaded POC. Never bundled into initial chunk.
const DesignLabRouter = lazy(lazyWithRetry(() => import("./pages/design-lab-v2/DesignLabRouter")));
// OS V3 · M1 — Page de revue des propositions identitaires (lazy, hors nav)
const BrandProposalsPage = lazy(lazyWithRetry(() => import("./pages/BrandProposals")));
const DesignLabV21Router = lazy(
  lazyWithRetry(() =>
    import("./pages/design-lab-v2/DesignLabRouter").then((m) => ({ default: m.DesignLabV21Router }))
  )
);
const DesignLabV22Router = lazy(
  lazyWithRetry(() =>
    import("./pages/design-lab-v2/DesignLabRouter").then((m) => ({ default: m.DesignLabV22Router }))
  )
);
const OpportunityInterestsPage = lazy(lazyWithRetry(() => import("./pages/admin/OpportunityInterests")));
const RecurringSchedulesPage = lazy(lazyWithRetry(() => import("./pages/admin/RecurringSchedules")));
const RecurringScheduleDetailPage = lazy(lazyWithRetry(() => import("./pages/admin/RecurringScheduleDetail")));
const RecurringScheduleFormPage = lazy(lazyWithRetry(() => import("./pages/admin/RecurringScheduleForm")));
const AccountAdminPage = lazy(lazyWithRetry(() => import("./pages/admin/AccountAdmin")));
const MySessionsPage = lazy(lazyWithRetry(() => import("./pages/admin/MySessions")));
const AccountAdminDetailPage = lazy(lazyWithRetry(() => import("./pages/admin/AccountAdminDetail")));
const BulkAccountPrepPage = lazy(lazyWithRetry(() => import("./pages/admin/BulkAccountPrep")));
const RecycleBinPage = lazy(lazyWithRetry(() => import("./pages/admin/RecycleBin")));
const MovementsImportHistoryPage = lazy(lazyWithRetry(() => import("./pages/admin/MovementsImportHistory")));  // LOT 536 Phase 7-quater

// Sponsor Pages — lazy-loaded (Lot 354 bundle optimization)
const SponsorDashboard = lazy(lazyWithRetry(() => import("./pages/sponsor/Dashboard")));
const SponsorProjects = lazy(lazyWithRetry(() => import("./pages/sponsor/Projects")));
const SponsorProjectDetail = lazy(lazyWithRetry(() => import("./pages/sponsor/ProjectDetail")));
const SponsorDocuments = lazy(lazyWithRetry(() => import("./pages/sponsor/Documents")));
const SponsorObligations = lazy(lazyWithRetry(() => import("./pages/sponsor/Obligations")));
const SponsorProgress = lazy(lazyWithRetry(() => import("./pages/sponsor/Progress")));
const ProjectContributions = lazy(lazyWithRetry(() => import("./pages/sponsor/ProjectContributions")));
const SponsorProjectInvestors = lazy(lazyWithRetry(() => import("./pages/sponsor/ProjectInvestors")));
const SponsorProjectReporting = lazy(lazyWithRetry(() => import("./pages/sponsor/ProjectReporting")));

// Admin Contribution Review — lazy with named export support
const ContributionReview = lazy(lazyWithRetry(() => import("./pages/admin/ContributionReview")));
const ContributionListPage = lazy(lazyWithRetry(() =>
  import("./pages/admin/ContributionReview").then((m) => ({ default: m.ContributionListPage }))
));

// LOT 537 v63.2 — Lazy-loaded public + stub pages (Daniel 08/06/2026)
// Pages anciennement définies inline dans ce fichier (~1100 lignes). Sorties
// vers des modules dédiés et chargées à la demande → bundle initial allégé,
// chunks séparés par route. Aucun changement de logique ni de markup.
const AboutPage = lazy(lazyWithRetry(() => import("./pages/public/AboutPage")));
const TeamPage = lazy(lazyWithRetry(() => import("./pages/public/TeamPage")));
const HowItWorksPage = lazy(lazyWithRetry(() => import("./pages/public/HowItWorksPage")));
const ContactPage = lazy(lazyWithRetry(() => import("./pages/public/ContactPage")));
const PorteursProjetPage = lazy(lazyWithRetry(() => import("./pages/public/PorteursProjetPage")));
// PHASE 8 B4 — Hub éditorial public
const JournalPage = lazy(lazyWithRetry(() => import("./pages/public/JournalPage")));
const JournalArticlePage = lazy(lazyWithRetry(() => import("./pages/public/JournalArticlePage")));
// SPRINT 9 (M6/M10) — pages piliers (« guides ») et pages auteur publiques
const GuidePillarPage = lazy(lazyWithRetry(() => import("./pages/public/GuidePillarPage")));
// PUBLIC V2.1 — pages piliers « Implantations » (autorité éditoriale géographique)
const ImplantationPage = lazy(lazyWithRetry(() => import("./pages/public/ImplantationPage")));
const JournalAuthorPage = lazy(lazyWithRetry(() => import("./pages/public/JournalAuthorPage")));
// PHASE 8 B6 — Réalisations publiques (mini-reportages)
const RealisationsPage = lazy(lazyWithRetry(() => import("./pages/public/RealisationsPage")));
const RealisationDetailPage = lazy(lazyWithRetry(() => import("./pages/public/RealisationDetailPage")));
const DocumentsPage = lazy(lazyWithRetry(() => import("./pages/investor/DocumentsStubPage")));
const SponsorsPage = lazy(lazyWithRetry(() => import("./pages/admin/SponsorsListPage")));

// LOT 537 v63.2ter Vague 1 — Lazy-loaded layouts + auth/legal/SPV pages + admin overlays (Daniel 08/06/2026)
// Objectif : retirer ces modules du main bundle pour les visiteurs publics anonymes.
// Aucun changement métier, aucun changement de route, aucun changement i18n.
// B — Layouts privés (jamais chargés pour un visiteur public anonyme)
const AdminLayout = lazy(lazyWithRetry(() => import("./components/AdminLayout")));
const InvestorLayout = lazy(lazyWithRetry(() => import("./components/InvestorLayout")));
const SponsorLayout = lazy(lazyWithRetry(() => import("./components/SponsorLayout")));
// C — Pages juridiques (rarement visitées au premier chargement)
const MentionsLegales = lazy(lazyWithRetry(() => import("./pages/public/MentionsLegales")));
const PolitiqueCookies = lazy(lazyWithRetry(() => import("./pages/public/PolitiqueCookies")));
const ConditionsGenerales = lazy(lazyWithRetry(() => import("./pages/public/ConditionsGenerales")));
const AvertissementRisques = lazy(lazyWithRetry(() => import("./pages/public/AvertissementRisques")));
const PolitiqueConfidentialite = lazy(lazyWithRetry(() => import("./pages/public/PolitiqueConfidentialite")));
// D — Auth secondaires (LoginPage et HomePage restent eager — entry points critiques)
const RequestAccessPage = lazy(lazyWithRetry(() => import("./pages/public/RequestAccessPage")));
const ActivatePage = lazy(lazyWithRetry(() => import("./pages/public/ActivatePage")));
const VerifyDocumentPage = lazy(lazyWithRetry(() => import("./pages/public/VerifyDocument"))); // APEX M6.6
const ForgotPasswordPage = lazy(lazyWithRetry(() => import("./pages/public/ForgotPasswordPage")));
const ResetPasswordPage = lazy(lazyWithRetry(() => import("./pages/public/ResetPasswordPage")));
// E — Pages SPV publiques (PublicSpvDisabledPage est passée en lazy via PublicSpvGate)
const SpvOfferPage = lazy(lazyWithRetry(() => import("./pages/public/SpvOfferPage")));
const SpvIntakePage = lazy(lazyWithRetry(() => import("./pages/public/SpvIntakePage")));
// F — Badge admin (visible uniquement aux admins, ne doit pas alourdir le bundle public)
const BuildVersionBadge = lazy(lazyWithRetry(() => import("./components/BuildVersionBadge")));
// G — Modal session expirée (chargée uniquement au moment de l'affichage)
const SessionExpiredModal = lazy(lazyWithRetry(() => import("./components/SessionExpiredModal")));
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: 'instant' }); }, [pathname]);
  return null;
}

// Syncs profile language preference on auth
function LanguageSync() {
  const { user, isAuthenticated } = useAuth();
  const { syncFromProfile } = useI18n();
  const synced = React.useRef(false);

  React.useEffect(() => {
    if (isAuthenticated && user?.preferred_language && !synced.current) {
      syncFromProfile(user.preferred_language);
      synced.current = true;
    }
    if (!isAuthenticated) {
      synced.current = false;
    }
  }, [isAuthenticated, user, syncFromProfile]);

  return null;
}

function App() {
  // Lot 355 — Web Vitals tracker (passive, no PII, batched 5s + pagehide flush)
  useEffect(() => { initWebVitals(); }, []);

  return (
    <HelmetProvider>
    <I18nProvider>
    <AuthProvider>
    <InvestorProfileProvider>
      <div className="App">
        <BrowserRouter>
          {/* LOT 470 — Skip-to-content link for keyboard users */}
          <SkipToContent />
          <ScrollToTop />
          <LanguageSync />
          <ImpersonationBanner />
          {/* LOT 537 v63.2ter — F+G : BuildVersionBadge et SessionExpiredModal sont
              désormais lazy. Suspense fallback={null} (zéro UI, ils sont déjà
              conditionnels et non-bloquants). */}
          <Suspense fallback={null}>
            <SessionExpiredModal />
            <BuildVersionBadge />
          </Suspense>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Root → redirect to /fr (French default) */}
            <Route path="/" element={<LangRedirect toFr="/fr" toEn="/en" />} />

            {/* Old bare paths → redirect to language-prefixed versions */}
            <Route path="/about" element={<LangRedirect toFr="/fr/a-propos" toEn="/en/about" />} />
            <Route path="/team" element={<LangRedirect toFr="/fr/notre-equipe" toEn="/en/our-team" />} />
            <Route path="/how-it-works" element={<LangRedirect toFr="/fr/comment-ca-marche" toEn="/en/how-it-works" />} />
            <Route path="/contact" element={<LangRedirect toFr="/fr/contact" toEn="/en/contact" />} />
            <Route path="/request-access" element={<LangRedirect toFr="/fr/demander-acces" toEn="/en/request-access" />} />
            <Route path="/login" element={<LangRedirect toFr="/fr/connexion" toEn="/en/login" />} />
            <Route path="/mentions-legales" element={<LangRedirect toFr="/fr/mentions-legales" toEn="/en/legal-notice" />} />
            <Route path="/privacy" element={<LangRedirect toFr="/fr/politique-de-confidentialite" toEn="/en/privacy-policy" />} />
            <Route path="/cookies" element={<LangRedirect toFr="/fr/politique-de-cookies" toEn="/en/cookie-policy" />} />
            <Route path="/terms" element={<LangRedirect toFr="/fr/conditions-generales" toEn="/en/terms-of-use" />} />
            <Route path="/risk-disclosure" element={<LangRedirect toFr="/fr/avertissement-risques" toEn="/en/risk-disclosure" />} />
            <Route path="/creer-spv" element={<LangRedirect toFr="/fr/creer-spv" toEn="/en/create-spv" />} />
            <Route path="/create-spv" element={<LangRedirect toFr="/fr/creer-spv" toEn="/en/create-spv" />} />
            <Route path="/p/:slug" element={<FreePageBareRedirect />} />

            {/* French public routes */}
            <Route path="/fr" element={<LocalizedPublicLayout />}>
              <Route index element={<HomePage />} />
              <Route path="a-propos" element={<AboutPage />} />
              <Route path="notre-equipe" element={<TeamPage />} />
              <Route path="comment-ca-marche" element={<HowItWorksPage />} />
              <Route path="journal" element={<JournalPage />} />
              {/* SPRINT 9 (M10/M6) — auteur AVANT :slug (segment réservé) + guides */}
              <Route path="journal/auteur/:slug" element={<JournalAuthorPage />} />
              <Route path="journal/:slug" element={<JournalArticlePage />} />
              <Route path="guides/:slug" element={<GuidePillarPage />} />
              {/* PUBLIC V2.1 — implantations (ensemble fermé, SSOT routes statiques) */}
              <Route path="implantations/bali" element={<ImplantationPage geo="bali" />} />
              <Route path="implantations/dubai" element={<ImplantationPage geo="dubai" />} />
              <Route path="implantations/andorre" element={<ImplantationPage geo="andorre" />} />
              <Route path="implantations/algarve" element={<ImplantationPage geo="algarve" />} />
              <Route path="implantations/sud-de-la-france" element={<ImplantationPage geo="sud-de-la-france" />} />
              <Route path="realisations" element={<RealisationsPage />} />
              <Route path="realisations/:slug" element={<RealisationDetailPage />} />
              <Route path="contact" element={<ContactPage />} />
              {/* AMBASSADEUR V1 — tunnel noindex, HORS sitemap/prerender */}
              <Route path="recommander" element={<ReferralLanding />} />
              <Route path="porteurs-de-projet" element={<PorteursProjetPage />} />
              <Route path="demander-acces" element={<RequestAccessPage />} />
              <Route path="connexion" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
              <Route path="mentions-legales" element={<MentionsLegales />} />
              <Route path="politique-de-confidentialite" element={<PolitiqueConfidentialite />} />
              <Route path="politique-de-cookies" element={<PolitiqueCookies />} />
              <Route path="conditions-generales" element={<ConditionsGenerales />} />
              <Route path="avertissement-risques" element={<AvertissementRisques />} />
              <Route path="creer-spv" element={<PublicSpvGate><SpvOfferPage /></PublicSpvGate>} />
              <Route path="creer-spv/intake" element={<PublicSpvGate><SpvIntakePage /></PublicSpvGate>} />
              {/* STUDIO B2 — pages libres composées par blocs (noindex par défaut) */}
              <Route path="p/:slug" element={<FreePagePublic />} />
              {/* SPRINT 8 — vraie 404 publique (fin du soft-404 qui rendait la home) */}
              <Route path="*" element={<PublicNotFoundPage />} />
            </Route>

            {/* English public routes */}
            <Route path="/en" element={<LocalizedPublicLayout />}>
              <Route index element={<HomePage />} />
              <Route path="about" element={<AboutPage />} />
              <Route path="our-team" element={<TeamPage />} />
              <Route path="how-it-works" element={<HowItWorksPage />} />
              <Route path="journal" element={<JournalPage />} />
              {/* SPRINT 9 (M10/M6) — auteur AVANT :slug (segment réservé) + guides */}
              <Route path="journal/auteur/:slug" element={<JournalAuthorPage />} />
              <Route path="journal/:slug" element={<JournalArticlePage />} />
              <Route path="guides/:slug" element={<GuidePillarPage />} />
              {/* PUBLIC V2.1 — locations (ensemble fermé, SSOT routes statiques) */}
              <Route path="locations/bali" element={<ImplantationPage geo="bali" />} />
              <Route path="locations/dubai" element={<ImplantationPage geo="dubai" />} />
              <Route path="locations/andorra" element={<ImplantationPage geo="andorre" />} />
              <Route path="locations/algarve" element={<ImplantationPage geo="algarve" />} />
              <Route path="locations/south-of-france" element={<ImplantationPage geo="sud-de-la-france" />} />
              <Route path="track-record" element={<RealisationsPage />} />
              <Route path="track-record/:slug" element={<RealisationDetailPage />} />
              <Route path="contact" element={<ContactPage />} />
              {/* AMBASSADOR V1 — noindex funnel, NOT in sitemap/prerender */}
              <Route path="refer" element={<ReferralLanding />} />
              <Route path="project-owners" element={<PorteursProjetPage />} />
              <Route path="request-access" element={<RequestAccessPage />} />
              <Route path="login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
              <Route path="legal-notice" element={<MentionsLegales />} />
              <Route path="privacy-policy" element={<PolitiqueConfidentialite />} />
              <Route path="cookie-policy" element={<PolitiqueCookies />} />
              <Route path="terms-of-use" element={<ConditionsGenerales />} />
              <Route path="risk-disclosure" element={<AvertissementRisques />} />
              <Route path="create-spv" element={<PublicSpvGate><SpvOfferPage /></PublicSpvGate>} />
              <Route path="create-spv/intake" element={<PublicSpvGate><SpvIntakePage /></PublicSpvGate>} />
              {/* STUDIO B2 — pages libres composées par blocs (noindex par défaut) */}
              <Route path="p/:slug" element={<FreePagePublic />} />
              {/* SPRINT 8 — vraie 404 publique */}
              <Route path="*" element={<PublicNotFoundPage />} />
            </Route>

            {/* Auth Routes (language-neutral utility) */}
            <Route path="/activate" element={<ActivatePage />} />
            {/* APEX M6.6 — Vérification publique d'authenticité (QR des PDF) */}
            <Route path="/verify/:numero" element={<VerifyDocumentPage />} />
            <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPasswordPage /></PublicOnlyRoute>} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />{/* RETOUR ÉMILIE 04/09 (cas 4) — PAS de PublicOnlyRoute : un utilisateur CONNECTÉ qui clique un lien de réinitialisation doit voir le formulaire (avant : redirection silencieuse vers son dashboard, perçue comme « lien cassé »). La page gère elle-même les sessions (bandeau + déconnexion propre). */}

            {/* Investor Portal — LOT 537 v15 : l'admin peut accéder à /portal
                directement pour voir & éditer la page Opportunités en mode WYSIWYG. */}
            <Route path="/portal" element={
              <ProtectedRoute allowedRoles={['investor', 'admin', 'super_admin']}>
                <InvestorLayout />
              </ProtectedRoute>
            }>
              <Route index element={<InvestorDashboard />} />
              <Route path="projects" element={<ErrorBoundary label="investor-projects-list"><ProjectsPage /></ErrorBoundary>} />
              <Route path="projects/:projectId" element={<ErrorBoundary label="investor-project-detail"><ProjectDetail /></ErrorBoundary>} />
              {/* APEX M1.6 — « Mon patrimoine » (fusion Portefeuille + Tous mes projets, Q4) */}
              <Route path="patrimoine" element={<PatrimoinePage />} />
              <Route path="subscriptions" element={<LegacyPatrimoineRedirect />} />
              <Route path="portfolio" element={<LegacyPatrimoineRedirect />} />
              <Route path="investments/:id" element={<InvestmentDetailPage />} />
              <Route path="investments/:id/complete" element={<SubscriptionCompletionPage />} />
              <Route path="updates" element={<InvestorUpdatesPage />} />
              <Route path="waiting-list" element={<WaitingListPage />} />
              <Route path="compliance" element={<CompliancePage />} />
              <Route path="kyb" element={<PortalKybPage />} />
              <Route path="documents" element={<InvestorDocumentsPage />} />
              <Route path="movements" element={<MovementsPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="profile" element={<ProfilePage />} />
              {/* GO PO V3 — Espace investisseur « Ambassadeur » */}
              <Route path="ambassador" element={<ErrorBoundary label="investor-ambassador"><InvestorAmbassadorPage /></ErrorBoundary>} />
              <Route path="secondary-market" element={<InvestorSecondaryMarketPage />} />
              <Route path="reporting" element={<InvestorReportingCenter />} />
              <Route path="issues" element={<InvestorIssuesPage />} />
              <Route path="messages" element={<InvestorMessagesPage />} />
              <Route path="messages/:threadId" element={<InvestorMessagesPage />} />
              <Route path="news" element={<InvestorNewsPage />} />
              <Route path="notification-preferences" element={<NotificationPreferencesPage />} />
            </Route>

            {/* OS V3 · M1 — Propositions identitaires WMGRA V3 (page de revue PO,
                non liée dans la nav, aucun accès données — SVG statiques). */}
            <Route path="/brand-proposals" element={<BrandProposalsPage />} />

            {/* MFA Enrollment — accessible with limited mfa_setup token, outside admin auth guard */}
            <Route path="/admin/mfa-enrollment" element={<MfaEnrollmentPage />} />
            {/* APEX M2 — enrôlement MFA universel (investisseurs à MFA imposée + admins) */}
            <Route path="/mfa-enrollment" element={<MfaEnrollmentPage />} />

            {/* Design Lab v2 — POC only · super_admin only · lazy-loaded · zero impact on production */}
            <Route
              path="/design-lab/v2/*"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <DesignLabRouter />
                </ProtectedRoute>
              }
            />
            {/* Design Lab v2.1 — more audacious direction, same isolation rules */}
            <Route
              path="/design-lab/v21/*"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <DesignLabV21Router />
                </ProtectedRoute>
              }
            />
            {/* Design Lab v2.2 — Tech Refresh based on prod structure, same isolation rules */}
            <Route
              path="/design-lab/v22/*"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <DesignLabV22Router />
                </ProtectedRoute>
              }
            />

            {/* Admin Portal */}
            {/* Daily task home; the detailed cockpit and all legacy links remain available. */}
            <Route path="/admin" element={<Navigate to="/admin/ma-journee" replace />} />
            <Route path="/admin/*" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                <AdminLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="/admin/ma-journee" replace />} />
              {/* LOT quick-wins-admin-emilie · QW#5 — Audit documentaire
                  (aucune suppression) :
                  - `/admin/dashboard` : redirect frontend vers Cockpit OS
                    conservé pour signets historiques. L'endpoint backend
                    `/api/admin/dashboard` (server_legacy.py:1539) est
                    INTOUCHABLE et est utilisé par 5+ fichiers de tests
                    (dont test_lot343g, test_lot535, test_expiry_monitoring).
                  - `/admin/tasks` : activement référencé par
                    OnboardingConsole.js:712, CommandCenter.js:167,239,313
                    et SponsorOps.js:634 — NE PAS retirer sans plan de
                    migration coordonné (dépasse le scope quick-wins).
                  - `/admin/crm` : activement référencé par
                    Investors.js:276 (bouton CRM) — NE PAS retirer sans
                    plan de migration coordonné. */}
              <Route path="dashboard" element={<Navigate to="/admin/cockpit-os" replace />} />
              <Route path="access-requests" element={<AccessRequestsPage />} />
              <Route path="investors" element={<InvestorsPage />} />
              <Route path="investors/:investorId" element={<AdminInvestorDetailPage />} />
              <Route path="investor-preview" element={<InvestorPortalPreview />} />
              <Route path="messages" element={<AdminMessagesPage />} />
              <Route path="messages/:threadId" element={<AdminMessagesPage />} />
              {/* LOT back-office-compliance-p1 · Phase 3 — Rebrand routing
                  compliance (Q1=A validé PO). `/admin/compliance` héberge
                  désormais le portail conformité (`ComplianceHome`). Le
                  Centre documentaire reste servi via `/admin/documents`
                  (préservé L+1). Ordre : `/compliance/kyc` et `/compliance/kyb`
                  DÉCLARÉS AVANT `/compliance/:profileId` pour éviter que le
                  slug littéral soit capturé par le param dynamique. */}
              <Route path="compliance" element={<ComplianceHomePage />} />
              <Route path="compliance/review" element={<ComplianceReviewQueuePage />} />
              <Route path="compliance/kyc" element={<ComplianceInvestorListPage />} />
              <Route path="compliance/kyb" element={<ComplianceInvestorListPage />} />
              <Route path="compliance/:profileId" element={<ComplianceInvestorDetailPage />} />
              <Route path="ma-journee" element={<MyDayPage />} />
              <Route path="site" element={<SiteWorkspacePage />} />
              <Route path="media" element={<MediaLibraryPage />} />
              {/* STUDIO B2 — Pages libres (CMS par blocs) */}
              <Route path="free-pages" element={<PermissionGuard perm="project.page_edit"><FreePagesAdminPage /></PermissionGuard>} />
              <Route path="free-pages/:slug" element={<PermissionGuard perm="project.page_edit"><FreePageEditorPage /></PermissionGuard>} />
              {/* STUDIO B4 — Champs personnalisés (méta, super_admin) */}
              <Route path="studio/fields" element={<PermissionGuard perm="studio.fields_manage"><StudioFieldsPage /></PermissionGuard>} />
              <Route path="documents" element={<DocumentCenterPage />} />
              <Route path="updates" element={<UpdateCenterPage />} />
              <Route path="updates/:updateId" element={<UpdateDetailPage />} />
              {/* APEX SPRINT01 B1 — Hub Publications (2 canaux). Anciennes
                  routes redirigées, garde-fou visuel de canal dans le hub. */}
              <Route path="publications" element={<PublicationsHubPage />} />
              {/* ACQUISITION V3 — écran décisionnel organique (SSOT insights) */}
              <Route path="acquisition" element={<PermissionGuard perm="project.page_edit"><AcquisitionInsightsPage /></PermissionGuard>} />
              {/* ACQUISITION ENGINE V1 — CRM interne (permissions dédiées leads.view) */}
              <Route path="acquisition/journee" element={<PermissionGuard perm="leads.view"><MaJourneePage /></PermissionGuard>} />
              <Route path="acquisition/executive" element={<PermissionGuard perm="leads.view"><AcquisitionExecutivePage /></PermissionGuard>} />
              <Route path="acquisition/leads" element={<PermissionGuard perm="leads.view"><LeadsPipelinePage /></PermissionGuard>} />
              <Route path="acquisition/leads/:leadId" element={<PermissionGuard perm="leads.view"><LeadDetailPage /></PermissionGuard>} />
              {/* PROGRAMME AMBASSADEUR V1 — kill-switch backend module_enabled */}
              <Route path="ambassadors" element={<PermissionGuard perm="ambassador.view"><AmbassadorsPage /></PermissionGuard>} />
              <Route path="ambassadors/:ambId" element={<PermissionGuard perm="ambassador.view"><AmbassadorDetail /></PermissionGuard>} />
              <Route path="news" element={<RedirectWithQuery to="/admin/publications" params={{ tab: 'actualites' }} />} />
              <Route path="audit/deleted" element={<RecycleBinPage />} />
              <Route path="projects" element={<ProjectsAdminPage />} />
              <Route path="projects/health" element={<ProjectsHealthPage />} />
              <Route path="projects/:projectId" element={<ErrorBoundary label="admin-project-workspace"><ProjectWorkspacePage /></ErrorBoundary>} />
              <Route path="projects/:projectId/workspace" element={<Navigate to=".." replace />} />
              <Route path="projects/:projectId/console" element={<ErrorBoundary label="admin-project-detail"><AdminProjectDetailPage /></ErrorBoundary>} />
              <Route path="projects/:projectId/studio" element={<LegacyStudioRedirect />} />
              {/* STUDIO B1 — Assistant de création avec provisioning automatique.
                  Le formulaire classique reste accessible (zéro régression). */}
              <Route path="projects/new" element={<PermissionGuard perm="project.create"><ProjectWizardPage /></PermissionGuard>} />
              <Route path="projects/new/classic" element={<PermissionGuard perm="project.create"><ProjectStudioPage /></PermissionGuard>} />
              <Route path="projects/:projectId/edit" element={<LegacyStudioRedirect />} />
              <Route path="subscriptions" element={<SubscriptionsAdminPage />} />
              <Route path="subscriptions/:id" element={<SubscriptionDetailAdminPage />} />
              <Route path="waiting-list" element={<WaitingListAdminPage />} />
              <Route path="movements" element={<MovementsAdminPage />} />
              <Route path="movements/import-history" element={<MovementsImportHistoryPage />} />  {/* LOT 536 Phase 7-quater */}
              <Route path="reconciliation" element={<AdminReconciliation />} />
              <Route path="qa-console" element={<QAConsolePage />} />
              <Route path="sponsors" element={<SponsorsPage />} />
              <Route path="notifications" element={<Navigate to="/admin/activity?tab=notifications" replace />} />
              <Route path="audit-logs" element={<AuditLogPage />} />
              <Route path="internal-users" element={<PermissionGuard perm="admin.internal_users"><InternalUsersPage /></PermissionGuard>} />
              {/* APEX FIABILITÉ — Diagnostic Patrimoine (le backend refuse les
                  non-super_admin ; la page affiche l'erreur 403 explicitement). */}
              <Route path="diagnostic-patrimoine" element={<DiagnosticPatrimoinePage />} />
              <Route path="secondary-market" element={<AdminSecondaryMarketPage />} />
              <Route path="import" element={<PermissionGuard perm="admin.data_tools"><ImportWizard /></PermissionGuard>} />
              {/* LOT quick-wins-admin-emilie · QW#1 — L'entrée sidebar
                  `import-legacy` a été retirée : le Wizard nouveau
                  `/admin/import` est source unique depuis LOT 536.
                  La route ici est CONSERVÉE et redirige (301 côté React
                  Router via Navigate) pour préserver anciens signets et
                  liens historiques. Le backend `routes/import_toolkit.py`
                  reste INTACT (utilisé par services parsers). */}
              <Route path="import-legacy" element={<Navigate to="/admin/import" replace />} />
              <Route path="reports" element={<PermissionGuard perm="admin.data_tools"><ReportsAdmin /></PermissionGuard>} />
              <Route path="go-live" element={<GoLivePage />} />
              <Route path="system-health" element={<GoLiveControlCenterPage />} />
              <Route path="backups-lot358" element={<BackupsLot358Page />} />
              <Route path="linked-user-requests" element={<LinkedUserRequests />} />
              <Route path="holder-requests" element={<HolderRequests />} />
              <Route path="sponsor-ops" element={<SponsorOpsPage />} />
              <Route path="projects/:projectId/operations" element={<ProjectOperationsPage />} />
              <Route path="projects/:projectId/communications" element={<ProjectCommunicationsPage />} />
              <Route path="projects/:projectId/distributions" element={<DistributionManagementPage />} />
              <Route path="distributions/calendar" element={<DistributionCalendarPage />} />
              <Route path="recurring-schedules" element={<RecurringSchedulesPage />} />
              <Route path="recurring-schedules/new" element={<RecurringScheduleFormPage />} />
              <Route path="recurring-schedules/:scheduleId" element={<RecurringScheduleDetailPage />} />
              <Route path="recurring-schedules/:scheduleId/edit" element={<RecurringScheduleFormPage />} />
              <Route path="tasks" element={<Navigate to="/admin/cockpit-os" replace />} />
              {/* LOT 3 · S3-B — Renommage UX : /admin/templates → /admin/playbooks (redirect applicatif SPA, pas HTTP 301). */}
              {/* Le CTA `from-template-btn` de Projects.js:149 conserve son URL historique — le redirect fait le job. */}
              {/* La page Playbooks (Système A) est isolée des Templates e-mail transactionnels (Système B, route /admin/email-templates). */}
              <Route path="playbooks" element={<TemplateConsolePage />} />
              <Route path="templates" element={<Navigate to="/admin/playbooks" replace />} />
              <Route path="dictionary" element={<RedirectWithQuery to="/admin/settings" params={{ tab: 'dictionnaire' }} />} />  {/* B6 → hub Paramètres */}
              {/* LOT 4 · S4-B — command-center fusionné dans cockpit-os.
                  Route historique conservée en redirect SPA (pas HTTP 301).
                  PHASE 10 B-0 (L2) — CommandCenter.js supprimé (code mort). */}
              <Route path="command-center" element={<Navigate to="/admin/cockpit-os" replace />} />
              <Route path="onboarding" element={<OnboardingConsolePage />} />
              <Route path="security" element={<RedirectWithQuery to="/admin/settings" params={{ tab: 'securite' }} />} />
              <Route path="automation" element={<RedirectWithQuery to="/admin/settings" params={{ tab: 'automatisations' }} />} />
              <Route path="cockpit" element={<Navigate to="/admin/operations" replace />} />
              <Route path="operations" element={<OperationsPipeline />} />
              {/* SPRINT 2 §6.2 — l'ex page /admin/pipeline est fusionnée
                  comme onglet de la liste des souscriptions (query préservée). */}
              <Route path="pipeline" element={<RedirectWithQuery to="/admin/subscriptions" params={{ tab: 'pipeline' }} />} />
              <Route path="spv-intakes" element={<SpvIntakes />} />
              <Route path="vehicles" element={<Vehicles />} />
              <Route path="vehicles/:vehicleId" element={<VehicleDetail />} />
              <Route path="crm" element={<Navigate to="/admin/cockpit-os" replace />} />
              <Route path="contributions" element={<ContributionListPage />} />
              <Route path="contributions/:contributionId" element={<ContributionReview />} />
              <Route path="projects/:projectId/page-editor" element={<LegacyStudioRedirect forceTab="preview" />} />
              <Route path="projects/:projectId/preview" element={<LegacyStudioRedirect forceTab="preview" />} />
              {/* APEX SPRINT01 B6 — Hub Paramètres : 5 sous-pages centralisées.
                  Le hub filtre lui-même les onglets par permission (canDo). */}
              <Route path="settings" element={<SettingsHubPage />} />
              <Route path="health" element={<PermissionGuard perm="admin.settings"><ProductionHealth /></PermissionGuard>} />
              <Route path="data-ops" element={<PermissionGuard perm="admin.settings"><DataOps /></PermissionGuard>} />
              <Route path="data-hygiene" element={<PermissionGuard perm="admin.settings"><DataHygienePage /></PermissionGuard>} />
              <Route path="subscriptions/anomalies" element={<PermissionGuard perm="admin.settings"><SubscriptionAnomaliesPage /></PermissionGuard>} />
              <Route path="anomalies" element={<AnomaliesToFixPage />} />
              <Route path="subscription-anomalies" element={<Navigate to="/admin/subscriptions/anomalies" replace />} />  {/* phase1b — lien legacy */}
              <Route path="kyb" element={<PermissionGuard perm="operations.allocation_confirm"><KybDashboardPage /></PermissionGuard>} />
              <Route path="ubo-registry" element={<PermissionGuard perm="operations.allocation_confirm"><UboRegistryPage /></PermissionGuard>} />
              <Route path="tax-classification" element={<PermissionGuard perm="operations.allocation_confirm"><TaxClassification /></PermissionGuard>} />
              <Route path="identity-resolution" element={<PermissionGuard perm="investor.edit_profile"><IdentityResolution /></PermissionGuard>} />
              {/* PHASE 10 B-1 — Command Deck absorbé en onglet « Portefeuille »
                  du Tableau de pilotage (arbitrage PO Q2, 19/07/2026). */}
              <Route path="command-deck" element={<Navigate to="/admin/cockpit-os?tab=portefeuille" replace />} />
              <Route path="cockpit-os" element={<ErrorBoundary label="cockpit-os"><CockpitOS /></ErrorBoundary>} />
              {/* PHASE 10 B-1 — Briefings ×3 fusionnés dans un hub unique (arbitrage PO Q6). */}
              <Route path="executive-briefing" element={<Navigate to="/admin/briefings-hub?tab=executif" replace />} />
              <Route path="ops-briefing" element={<Navigate to="/admin/briefings-hub?tab=operations" replace />} />
              <Route path="structural-quality" element={<StructuralQuality />} />
              <Route path="briefings" element={<Navigate to="/admin/briefings-hub?tab=console" replace />} />
              <Route path="briefings-hub" element={<BriefingsHubPage />} />
              {/* PHASE 10 B-1 — Vues activité ×3 fusionnées en Journal d'activité (arbitrage PO Q6). */}
              <Route path="recent-activity" element={<Navigate to="/admin/activity?tab=recente" replace />} />
              <Route path="activity" element={<ActivityJournalPage />} />
              {/* PHASE 10 — index « Toutes les fonctions » + Cockpit Super Admin (B8). */}
              <Route path="all-functions" element={<AllFunctionsPage />} />
              <Route path="system-cockpit" element={<ProtectedRoute allowedRoles={['super_admin']}><SystemCockpitPage /></ProtectedRoute>} />
              {/* DOC RELIABILITY V1 — santé documentaire (diagnostic super admin < 1 min) */}
              <Route path="document-health" element={<ProtectedRoute allowedRoles={['super_admin']}><DocumentHealthPage /></ProtectedRoute>} />
              {/* LOT 497 — Diagnostic de session (accessible à tout admin) */}
              <Route path="diagnostic" element={<SessionDiagnostic />} />
              {/* LOT 498 — Fusion de comptes investisseurs en doublon (super_admin only) */}
              <Route path="users-merge" element={<UsersMerge />} />
              <Route path="site-editor" element={<PermissionGuard perm="project.page_edit"><SiteEditorPage /></PermissionGuard>} />
              <Route path="editorial" element={<RedirectWithQuery to="/admin/publications" params={{ tab: 'journal' }} />} />
              <Route path="email-templates" element={<RedirectWithQuery to="/admin/settings" params={{ tab: 'emails' }} />} />
              <Route path="i18n-overrides" element={<PermissionGuard perm="project.page_edit"><I18nOverridesPage /></PermissionGuard>} />
              <Route path="cms-coverage" element={<PermissionGuard perm="project.page_edit"><CmsCoverageMapPage /></PermissionGuard>} />
              <Route path="cms-pricing" element={<ProtectedRoute allowedRoles={['super_admin']}><CmsPricingPage /></ProtectedRoute>} />
              <Route path="cms" element={<CmsIndexPage />} />
              <Route path="cms-company-info" element={<CmsCompanyInfoPage />} />
              <Route path="homepage-projects" element={<PermissionGuard perm="project.page_edit"><HomepageProjectsAdminPage /></PermissionGuard>} />
              <Route path="activity-center" element={<Navigate to="/admin/activity?tab=centre" replace />} />
              <Route path="opportunity-interests" element={<OpportunityInterestsPage />} />
              <Route path="account-admin" element={<PermissionGuard perm="account.recovery"><AccountAdminPage /></PermissionGuard>} />
              <Route path="account-admin/bulk" element={<PermissionGuard perm="bulk.account_preparation"><BulkAccountPrepPage /></PermissionGuard>} />
              <Route path="account-admin/:uid" element={<PermissionGuard perm="account.recovery"><AccountAdminDetailPage /></PermissionGuard>} />
              <Route path="my-sessions" element={<MySessionsPage />} />
              {/* SPRINT 6 (recette FAIL 2) — alias « nom d'API » vers la page réelle. */}
              <Route path="kyb-dashboard" element={<Navigate to="/admin/kyb" replace />} />
              {/* SPRINT 6 — contrat « jamais rien » : une URL admin inconnue
                  n'affiche plus un espace vide, mais un état honnête. */}
              <Route path="*" element={<AdminNotFound />} />
            </Route>

            {/* Sponsor Portal */}
            <Route path="/sponsor" element={
              <ProtectedRoute allowedRoles={['sponsor']}>
                <SponsorLayout />
              </ProtectedRoute>
            }>
              <Route index element={<SponsorDashboard />} />
              <Route path="projects" element={<SponsorProjects />} />
              <Route path="projects/:projectId" element={<SponsorProjectDetail />} />
              <Route path="projects/:projectId/investors" element={<SponsorProjectInvestors />} />
              <Route path="projects/:projectId/reporting" element={<SponsorProjectReporting />} />
              <Route path="projects/:projectId/contributions" element={<ProjectContributions />} />
              <Route path="documents" element={<SponsorDocuments />} />
              <Route path="obligations" element={<SponsorObligations />} />
              <Route path="progress" element={<SponsorProgress />} />
            </Route>

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/fr" replace />} />
          </Routes>
          </Suspense>
          <CookieConsent />
        </BrowserRouter>
        <Toaster />
        <GlobalConfirmHost />
      </div>
    </InvestorProfileProvider>
    </AuthProvider>
    </I18nProvider>
    </HelmetProvider>
  );
}

export default App;
